import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TransactionsInfoListEmpty from '@/features/finances/components/Transactions/TransactionsInfo/TransactionsInfoListEmpty';
import EActivitiesListSkeleton from '@/components/everridge/activities/EActivitiesListSkeleton';
import TransactionDetails from '@/features/transactions/components/TransactionDetails';
import { useFinancesTransactionsApi } from '@/features/finances/hooks/useFinancesTransactionsApi';
import EActivitiesElement from '@/components/everridge/activities/EActivitiesElement';
import { cn } from '@/lib/utils';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { TransactionsUtils } from '@/features/transactions/utils/transactionsUtils';
import { useTransactionsFilter } from '@/features/transactions/hooks/useTransactionsFilter';

type Props = {
    apartmentName?: string;
    apartmentId?: string;
    ownerId?: string;
    isMobile?: boolean;
};

const TransactionsInfoList: FC<Props> = ({ apartmentName, apartmentId, ownerId, isMobile }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [transactionId, setTransactionId] = useState('');

    const navigate = useNavigate();
    const { t } = useTranslation();

    const { transactionsData, isLoadingTransactionsData } = useFinancesTransactionsApi({
        apartmentId,
        ownerId,
    });

    const showTransactionHandler = (id: string) => {
        setTransactionId(id);
        setShowDetails(true);
    };

    const { setFilter, resetFilter } = useTransactionsFilter();

    const handleNavigateTransactions = () => {
        if (apartmentId) {
            const apartment = [
                {
                    name: apartmentName,
                    id: apartmentId,
                },
            ];

            setFilter({
                apartmentId: apartment,
            });
        } else {
            resetFilter();
        }

        const url = ownerId
            ? `/finances/transactions?ownerId=${ownerId}`
            : `/finances/transactions`;
        navigate(url);
    };

    const transactionsValues = Object.entries(transactionsData?.transactions ?? {});
    return (
        <div className="grow flex flex-col gap-4">
            <div className="flex-1">
                {isLoadingTransactionsData && <EActivitiesListSkeleton isMobile={isMobile} />}
                {!isLoadingTransactionsData && !!transactionsValues.length && (
                    <div className={cn('flex flex-col gap-6', isMobile && 'gap-4')}>
                        {transactionsValues.map(([title, events]) => {
                            return (
                                <div key={title}>
                                    <h5 className="text-sm text-popover-800">
                                        {DateTimeHelper.formatDate(new Date(title), 2)}
                                    </h5>
                                    {events.map((event) => {
                                        const { icon, colorClass } =
                                            TransactionsUtils.getTypeConfig(event.transactionType);

                                        const { label: feeLabel } = TransactionsUtils.getFeeConfig(
                                            event.sign,
                                        );

                                        const descriptionText = `${
                                            event.apartmentName
                                        }\u00A0\u00A0\u00A0·\u00A0\u00A0\u00A0${t(`${feeLabel}`)}`;

                                        return (
                                            <EActivitiesElement
                                                key={event.id}
                                                icon={icon}
                                                colorClass={colorClass}
                                                header={
                                                    event.serviceName ??
                                                    t(
                                                        'main.finances.components.transactions.noServices',
                                                    )
                                                }
                                                description={descriptionText}
                                                value={event.amount}
                                                id={event.id}
                                                onClick={showTransactionHandler}
                                                isMobile={isMobile}
                                                asset={event.transactionType}
                                            />
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                )}
                {!isLoadingTransactionsData && !transactionsValues.length && (
                    <TransactionsInfoListEmpty />
                )}
            </div>
            <Button
                variant={'outline'}
                className="w-full text-sm font-medium"
                onClick={handleNavigateTransactions}
            >
                {t('main.finances.components.transactions.seeMoreButton')}
            </Button>
            {showDetails && (
                <TransactionDetails
                    id={transactionId}
                    onClose={() => setShowDetails(false)}
                    isMobile={isMobile}
                />
            )}
        </div>
    );
};

export default TransactionsInfoList;
